import type { ReviewDecision, ReviewField, ReviewLine, Sourced } from '@console/types'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError } from '../api.js'
import { useRun } from '../runs/queries.js'
import { EvidenceLine, Highlighted } from '../review/EvidenceDisplay.js'
import { approveBlockedSays, approveGate, booksEffectSays } from '../review/gate.js'
import { readableItemValue } from '../review/item-label.js'
import { useReviewDraft, useSubmitDecisions } from '../review/queries.js'
import {
  AsOfLine,
  Button,
  DisclosureSection,
  Fingerprint,
  HairlineRule,
  Skeleton,
} from '../ui/index.js'
import styles from './ReviewPage.module.css'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE REVIEW SCREEN — `SPEC-LGH-001`.
 *
 * ── THE RENDERING RULE, WHICH IS THE PART MOST EASILY LOST ─────────────────
 * **The row is the unit of reading; the field is the unit of deciding.**
 *
 * A quote line is one thing in the operator's head — it is what the customer
 * wrote — so it stays one row. Each field renders as what it is: `grounded`
 * resolved and quiet, `asked` and `unmatched` as the question, in place, in the
 * column where the answer will go.
 *
 * **The row must never collapse to its least-resolved field.** The drafter
 * keeps `ParsedLine`'s three fields independent on stated grounds — *"an RFQ
 * that names an item and a quantity but no price is three-quarters readable,
 * and collapsing that to 'line unreadable' would throw away work the customer
 * already did and ask them things they already told us."* A screen that
 * flattens that at render time discards exactly what the type was shaped to
 * preserve, one layer up.
 *
 * ── AND NEVER INFER A CLASS ────────────────────────────────────────────────
 * Every branch below switches on `field.kind`, which the platform emitted.
 * Nothing here reads `candidates.length` to decide what a field *is*. It reads
 * it to decide whether to draw a picker, which is presentation and cannot
 * contradict the platform.
 * ═══════════════════════════════════════════════════════════════════════════
 */

const FIELD_LABEL: Record<ReviewField, string> = {
  item: 'Item',
  quantity: 'Quantity',
  unitPrice: 'Unit price',
}

interface FieldProps {
  line: ReviewLine
  field: ReviewField
  value: Sourced
  onDecide: (decision: ReviewDecision) => void
  disabled: boolean
}

function Field({ line, field, value, onDecide, disabled }: FieldProps) {
  const [text, setText] = useState('')

  if (value.kind === 'grounded') {
    return (
      <div className={styles['field']} data-kind="grounded">
        <span className={styles['resolved']}>
          {readableItemValue(field, value.value, value.evidence)}
        </span>
        <EvidenceLine evidence={value.evidence} />
      </div>
    )
  }

  if (value.kind === 'unreadable') {
    return (
      <div className={styles['field']} data-kind="unreadable">
        <p role="status" className={styles['unreadable']}>
          {value.says}
        </p>
      </div>
    )
  }

  const askedOrUnmatched = value.kind === 'asked' ? value.question : value.reason.says
  // Presentation, not classification: a picker appears when there is something
  // to pick. `kind` already told us what this field is.
  const candidates = value.kind === 'asked' ? value.candidates : []

  return (
    <div className={styles['field']} data-kind={value.kind}>
      <p className={styles['question']}>{askedOrUnmatched}</p>

      {/*
        `LGH-036`. A tie is a first-class case, not an edge case
        (`PLT-081`'s ruling) — more than one candidate here means the
        drafter could not rule the rest out, and the panel says so rather
        than presenting a ranked list with no explanation for why there's
        more than one row.
      */}
      {candidates.length > 1 && (
        <p className={styles['tieNotice']}>
          {candidates.length} candidates — none ruled out, ranked below
        </p>
      )}

      {candidates.map((candidate) => (
        <button
          key={candidate.candidateId}
          type="button"
          className={styles['candidate']}
          disabled={disabled}
          onClick={() => {
            onDecide({
              lineId: line.lineId,
              field,
              choice: { kind: 'candidate', candidateId: candidate.candidateId },
            })
          }}
        >
          <span className={styles['candidateValue']}>
            {readableItemValue(field, candidate.value, candidate.evidence)}
          </span>
          <EvidenceLine evidence={candidate.evidence} as="span" />
        </button>
      ))}

      <div className={styles['freeText']}>
        <label>
          <span className={styles['srOnly']}>
            {FIELD_LABEL[field]} for {line.rawText}
          </span>
          <input
            value={text}
            disabled={disabled}
            placeholder={candidates.length > 0 ? 'or type a different one' : 'type the answer'}
            onChange={(event) => {
              setText(event.target.value)
            }}
          />
        </label>
        <Button
          variant="secondary"
          disabled={disabled || text.trim() === ''}
          onClick={() => {
            onDecide({
              lineId: line.lineId,
              field,
              choice: { kind: 'freeText', text: text.trim() },
            })
            setText('')
          }}
        >
          Use this
        </Button>
        {field === 'item' && (
          // `add_to_catalog` is item-only, and the contract makes it
          // unrepresentable elsewhere — so this button exists on one field.
          <Button
            variant="secondary"
            disabled={disabled || text.trim() === ''}
            onClick={() => {
              onDecide({
                lineId: line.lineId,
                field,
                choice: { kind: 'addToCatalog', text: text.trim() },
              })
              setText('')
            }}
          >
            Add to catalogue
          </Button>
        )}
      </div>
    </div>
  )
}

export function ReviewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const runId = id ?? ''
  const draftQuery = useReviewDraft(runId)
  const submit = useSubmitDecisions(runId)
  /**
   * `LGH-027`. What the RUN offers, not what the draft has left outstanding
   * — the same `next_actions` `MutateRunPage` already gates on. Fetched
   * alongside the draft rather than derived from it, because the two are
   * genuinely different objects: `PLT-048`'s ruling on
   * `REQ-lighthouse-a-resolved-draft-cannot-be-approved`.
   */
  const runQuery = useRun(runId)

  /**
   * THE RENDER GATE'S OTHER HALF.
   *
   * Set in an effect, so it is only ever the fingerprint of a draft this
   * component has actually painted — never one it merely received. `approveGate`
   * holds the rule; this holds the fact.
   */
  const [renderedFingerprint, setRenderedFingerprint] = useState<string | null>(null)
  const draft = draftQuery.data
  useEffect(() => {
    if (draft !== undefined) setRenderedFingerprint(draft.fingerprint)
  }, [draft])

  const conflict =
    submit.error instanceof ApiError && submit.error.status === 409 ? submit.error : null
  /**
   * `LGH-030`. Every OTHER submission failure — chiefly the 422 either the
   * console's own field-shape guard or, since `PLT-048`, the platform's own
   * "this batch cannot be assembled" refusal produces — used to render
   * nothing at all. The operator's decision silently failed to save, and the
   * screen looked exactly as it did before they clicked. Nothing about the
   * draft moved underneath them (unlike `conflict`), so there is nothing to
   * reload — only something to say.
   */
  const rejection =
    submit.error instanceof ApiError && submit.error.status !== 409 ? submit.error : null

  function decide(decision: ReviewDecision) {
    if (draft === undefined) return
    // One answer, one call. Per-call identity approaches per-action identity as
    // closely as the authentication allows, and partial decisions are expected
    // — `outstanding` lives platform-side, so a half-finished review survives.
    submit.mutate({
      draftFingerprint: draft.fingerprint,
      acceptAllKnown: false,
      decisions: [decision],
    })
  }

  if (id === undefined) return <p role="alert">No run id in the URL.</p>
  /**
   * FOUND LIVE, `LGH-038` — A QUERY THAT NEVER STOPS BEING "PENDING".
   *
   * React Query's default `networkMode: 'online'` PAUSES a fetch rather than
   * failing it the moment it believes the network is down — `fetchStatus`
   * becomes `'paused'`, but `status` (and therefore `isPending`) stays
   * exactly as it was before the fetch even started. Gating only on
   * `isPending`, as this screen used to, cannot tell "still loading" apart
   * from "gave up trying to load, silently, forever" — confirmed live: a run
   * whose read fails left this screen on `<Skeleton />` indefinitely, no
   * error, no retry, matching `RequireAuth.tsx`'s own standing warning about
   * this exact hazard ("an indefinite pause looks exactly like the bug we
   * just fixed") almost word for word, on a screen that hadn't been fixed.
   *
   * Operator-console UX audit: `--stop` added to all three of this block's
   * own error paragraphs. Live-checked, these were the only substantive
   * error states left in the operator console with no colour at all — every
   * sibling screen (Home, Runs, Run Detail, Mutate) already renders its
   * `role="alert"` text in `--stop`.
   */
  if (draftQuery.fetchStatus === 'paused') {
    return (
      <main className={styles['page']}>
        <p role="alert" style={{ color: 'var(--stop)' }}>
          Could not reach the console server. Check your connection, then try again.
        </p>
        <Button onClick={() => void draftQuery.refetch()}>Try again</Button>
      </main>
    )
  }
  /**
   * Operator-console UX audit finding. This used to be a single bare,
   * unwrapped `<Skeleton />` — `width: 100%, height: 1em`, no page padding,
   * `background: color-mix(in srgb, var(--ink) 8%, var(--paper))` pulsing to
   * 55% opacity. Live-measured against the real retry ladder (a genuinely
   * 404ing run took several seconds to settle): for that whole window the
   * screen read as flatly blank, not "loading" — the one screen in this
   * console an operator opens specifically to make a judgement call.
   * Replaced with a skeleton sketching this screen's own shape (a header
   * plus the three-column field grid `.fields`/`.field` already define),
   * wrapped in the same `.page` padding every other state here already uses.
   */
  if (draftQuery.isPending) {
    return (
      <main className={styles['page']}>
        <div className={styles['header']}>
          <Skeleton width="14rem" height="1.5rem" />
          <Skeleton width="8rem" />
        </div>
        {[0, 1, 2].map((row) => (
          <div key={row} className={styles['line']}>
            <Skeleton width="60%" height="0.8rem" />
            <div className={styles['fields']}>
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
            </div>
          </div>
        ))}
      </main>
    )
  }
  if (draftQuery.isError) {
    return (
      <main className={styles['page']}>
        <p role="alert" style={{ color: 'var(--stop)' }}>
          {draftQuery.error.message}
        </p>
      </main>
    )
  }
  if (draft === undefined) {
    return (
      <main className={styles['page']}>
        <p role="alert" style={{ color: 'var(--stop)' }}>
          That run has nothing to review.
        </p>
      </main>
    )
  }

  const gate = approveGate({
    draft,
    renderedFingerprint,
    submitting: submit.isPending,
    offeredActions: runQuery.data?.availableActions,
  })

  const footerNotice = gate.can ? (
    <p className={styles['evidence']}>
      Approving version <Fingerprint value={gate.fingerprint} />. If this quote changes before you
      confirm, the approval is refused rather than applied to a version you never saw.
    </p>
  ) : runQuery.isError ? (
    <p role="alert">{runQuery.error.message}</p>
  ) : (
    <p className={styles['blocked']}>{approveBlockedSays(gate)}</p>
  )

  return (
    <main className={styles['page']}>
      <header className={styles['header']}>
        <h1>Review this quote</h1>
        {/*
          `LGH-027` restricted the FOOTER's fingerprint to when the run
          actually offers approval — correctly, since that sentence names
          what an approval binds to. But `SPEC-LGH-001` §3's own rule is
          broader than the approve control: the operator (and the ladder's
          rung 5) needs to see WHICH version is on screen throughout the
          review, not only at the moment it becomes approvable. This is that
          — always the draft actually rendered, never the approve gate's.
        */}
        <p className={styles['evidence']}>
          Reviewing version <Fingerprint value={draft.fingerprint} />.
        </p>
        {/*
          No as-of when the platform sent none — `LGH-021`.

          The contract's `ReviewResponse` is a bare object with no `as_of`,
          unlike every other read. **The refresh control stays either way**;
          it is the console's own action and needs no served fact. What is
          withheld is the CLAIM about when the data was true, because the
          console does not know it and the time of receipt is not the same
          thing.
        */}
        {draft.asOf === null ? (
          <Button variant="secondary" onClick={() => void draftQuery.refetch()}>
            Refresh
          </Button>
        ) : (
          <AsOfLine
            asOf={draft.asOf}
            onRefresh={() => {
              void draftQuery.refetch()
            }}
          />
        )}
      </header>

      {conflict !== null && (
        // The same conflict discipline `MutateRunPage` already renders: never a
        // silent retry against the stale version.
        <div role="alert" className={styles['conflict']}>
          <p>{conflict.message}</p>
          <Button
            onClick={() => {
              void draftQuery.refetch()
              submit.reset()
            }}
          >
            Reload this draft
          </Button>
        </div>
      )}

      {rejection !== null && (
        // `LGH-030`. Nothing to reload — the draft did not move — so unlike
        // `conflict` this has no button, only the platform's own reason and
        // an implicit invitation to try the field again.
        <div role="alert" className={styles['conflict']}>
          <p>{rejection.message}</p>
        </div>
      )}

      {draft.lines.map((line) => (
        <section key={line.lineId} className={styles['line']}>
          <p className={styles['rawText']}>
            <Highlighted
              text={line.rawText}
              span={
                line.quantity.kind === 'grounded' && line.quantity.evidence.kind === 'rfqSpan'
                  ? line.quantity.evidence.span
                  : ''
              }
            />
          </p>
          <div className={styles['fields']}>
            {(['item', 'quantity', 'unitPrice'] as const).map((field) => (
              <div key={field} className={styles['column']}>
                <span className={styles['columnLabel']}>{FIELD_LABEL[field]}</span>
                <Field
                  line={line}
                  field={field}
                  value={
                    field === 'item'
                      ? line.item
                      : field === 'quantity'
                        ? line.quantity
                        : line.unitPrice
                  }
                  onDecide={decide}
                  disabled={submit.isPending}
                />
              </div>
            ))}
          </div>
          <HairlineRule />
        </section>
      ))}

      <DisclosureSection title="What the drafter already resolved">
        <p className={styles['evidence']}>
          Everything shown without a question was read from your catalogue or from the
          customer&apos;s own words. Accepting them together does not touch anything still being
          asked.
        </p>
        <Button
          disabled={submit.isPending}
          onClick={() => {
            submit.mutate({
              draftFingerprint: draft.fingerprint,
              acceptAllKnown: true,
              decisions: [],
            })
          }}
        >
          Accept everything already resolved
        </Button>
      </DisclosureSection>

      <footer className={styles['footer']}>
        {/*
          `LGH-015` / `LGH-027`. The gate held the version and the operator
          could not see it — `MutateRunPage` has said so since Phase 6, and
          this screen now does too. The fingerprint quoted here used to be
          `draft.fingerprint` unconditionally, and that was the wrong object:
          it names the REVIEW draft, not the assembled quote approval actually
          binds to (`PLT-048`'s ruling). Quoting it only when the run has
          offered it is what stops this sentence promising an approval the
          next screen will refuse.
        */}
        {footerNotice}
        {gate.can && runQuery.data !== undefined && (
          // `books_effect`, rendered here and only here — the moment of
          // commitment, per the argument made for asking Cadence for it.
          <p className={styles['evidence']}>{booksEffectSays(runQuery.data.booksEffect)}</p>
        )}
        <Button
          disabled={!gate.can}
          onClick={() => {
            if (gate.can) navigate(`/runs/${runId}/approve`)
          }}
        >
          Approve this quote
        </Button>
      </footer>
    </main>
  )
}
